/**
 * Small lifecycle host used by EngineProvider. Plugins are ordinary objects so
 * browser apps can opt into instrumentation, URI protocols, telemetry, or
 * diagnostics without adding those concerns to the renderer itself.
 */
function normalizePlugins(plugins) {
    const result = [];
    const names = new Set();
    for (const plugin of plugins || []) {
        if (!plugin)
            continue;
        if (typeof plugin !== "object")
            throw new TypeError("Lens plugins must be objects");
        const name = String(plugin.name || "").trim();
        if (!name)
            throw new TypeError("Every lens plugin requires a name");
        if (names.has(name))
            throw new Error(`Duplicate lens plugin '${name}'`);
        names.add(name);
        result.push(plugin);
    }
    return result;
}
function pluginError(plugin, hook, error) {
    const message = error instanceof Error ? error.message : String(error);
    const wrapped = new Error(`Lens plugin '${plugin.name}' failed in ${hook}: ${message}`);
    wrapped.cause = error;
    return wrapped;
}
/**
 * @param {Array<Object>} [plugins]
 */
function callbackFor(plugin, hook) {
    const value = plugin[hook];
    return typeof value === "function" ? value : undefined;
}
function isRequestPatch(result) {
    return Boolean(result) && typeof result === "object" && (Object.hasOwn(result, "source")
        || Object.hasOwn(result, "options")
        || Object.hasOwn(result, "mode"));
}
export function createLensPluginHost(plugins = []) {
    const ordered = normalizePlugins(plugins);
    const services = new Map();
    const listeners = new Set();
    const disposers = [];
    let context = null;
    let active = false;
    let disposed = false;
    const notifyServicesChanged = () => {
        for (const listener of listeners)
            listener();
    };
    const host = {
        get active() { return active; },
        get plugins() { return ordered.slice(); },
        get(name) { return services.get(String(name)); },
        has(name) { return services.has(String(name)); },
        provide(name, service) {
            const key = String(name || "").trim();
            if (!key)
                throw new TypeError("Plugin service requires a name");
            services.set(key, service);
            notifyServicesChanged();
            return service;
        },
        revoke(name) {
            const removed = services.delete(String(name));
            if (removed)
                notifyServicesChanged();
            return removed;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        async activate(baseContext) {
            if (disposed)
                throw new Error("Lens plugin host is disposed");
            if (active)
                return;
            context = Object.freeze({ ...baseContext, plugins: host });
            const activeContext = context;
            for (const plugin of ordered) {
                if (typeof plugin.setup !== "function")
                    continue;
                let setupResult;
                try {
                    setupResult = await plugin.setup(activeContext);
                }
                catch (error) {
                    throw pluginError(plugin, "setup", error);
                }
                if (typeof setupResult === "function") {
                    disposers.push({ plugin, dispose: setupResult });
                }
                else if (setupResult && typeof setupResult === "object") {
                    if (Object.hasOwn(setupResult, "service"))
                        host.provide(plugin.name, setupResult.service);
                    if (typeof setupResult.dispose === "function") {
                        disposers.push({ plugin, dispose: setupResult.dispose });
                    }
                }
            }
            active = true;
            notifyServicesChanged();
        },
        async prepareLens(request) {
            if (!active)
                throw new Error("Lens plugin host is not active");
            if (!context)
                throw new Error("Lens plugin host context is unavailable");
            const activeContext = context;
            let current = { ...request };
            for (const plugin of ordered) {
                if (typeof plugin.beforeLensLoad !== "function")
                    continue;
                let result;
                try {
                    result = await plugin.beforeLensLoad(Object.freeze({ ...current }), activeContext);
                }
                catch (error) {
                    throw pluginError(plugin, "beforeLensLoad", error);
                }
                if (result !== undefined) {
                    current = isRequestPatch(result)
                        ? { ...current, ...result }
                        : { ...current, source: result };
                }
            }
            return current;
        },
        async emit(hook, payload) {
            if (!active)
                return;
            for (const plugin of ordered) {
                const callback = callbackFor(plugin, hook);
                if (!callback || !context)
                    continue;
                try {
                    await callback(payload, context);
                }
                catch (error) {
                    const wrapped = pluginError(plugin, hook, error);
                    if (typeof context?.onPluginError === "function")
                        context.onPluginError(wrapped, plugin, hook);
                    else
                        console.error(wrapped);
                }
            }
        },
        emitSync(hook, payload) {
            if (!active)
                return;
            for (const plugin of ordered) {
                const callback = callbackFor(plugin, hook);
                if (!callback || !context)
                    continue;
                try {
                    const result = callback(payload, context);
                    if (result instanceof Promise) {
                        result.catch((error) => {
                            const wrapped = pluginError(plugin, hook, error);
                            if (typeof context?.onPluginError === "function")
                                context.onPluginError(wrapped, plugin, hook);
                            else
                                console.error(wrapped);
                        });
                    }
                }
                catch (error) {
                    const wrapped = pluginError(plugin, hook, error);
                    if (typeof context?.onPluginError === "function")
                        context.onPluginError(wrapped, plugin, hook);
                    else
                        console.error(wrapped);
                }
            }
        },
        async dispose() {
            if (disposed)
                return;
            disposed = true;
            for (const entry of disposers.reverse()) {
                try {
                    await entry.dispose();
                }
                catch (error) {
                    const wrapped = pluginError(entry.plugin, "dispose", error);
                    if (typeof context?.onPluginError === "function")
                        context.onPluginError(wrapped, entry.plugin, "dispose");
                }
            }
            for (const plugin of ordered.slice().reverse()) {
                if (typeof plugin.dispose !== "function")
                    continue;
                try {
                    await plugin.dispose(context);
                }
                catch (error) {
                    const wrapped = pluginError(plugin, "dispose", error);
                    if (typeof context?.onPluginError === "function")
                        context.onPluginError(wrapped, plugin, "dispose");
                }
            }
            services.clear();
            active = false;
            notifyServicesChanged();
            listeners.clear();
        },
    };
    return host;
}
//# sourceMappingURL=pluginHost.js.map